import { apiJson } from './http';
import type { Page } from './news';

export type LandingViewport = 'mobile';

export type LandingCoordinate = {
  x: number;
  y: number;
};

export type LandingDimension = {
  width: number;
  height: number;
};

type Align = 'left' | 'center' | 'right';

export type LandingBlockBase<TType extends string = string> = {
  id: string;
  type: TType;
  position: LandingCoordinate;
  size?: LandingDimension;
  zIndex: number;
  locked?: boolean;
  rotation?: number;
};

export type LandingTextBlock = LandingBlockBase<'heading' | 'paragraph'> & {
  content: string;
  align: Align;
  color?: string;
  fontSize?: number;
  fontWeight?: 'regular' | 'medium' | 'bold';
  lineHeight?: number;
  fontFamily?: string;
};

export type LandingImageBlock = LandingBlockBase<'image'> & {
  url: string;
  alt?: string;
  objectFit?: 'cover' | 'contain';
  cornerRadius?: number;
  shadow?: boolean;
};

export type LandingVideoBlock = LandingBlockBase<'video'> & {
  url: string;
  posterUrl?: string | null;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  showControls?: boolean;
};

export type LandingBackgroundBlock = LandingBlockBase<'background'> & {
  color?: string;
  borderRadius?: number;
};

export type LandingFormFieldType = 'text' | 'email' | 'phone' | 'textarea';

export type LandingFormField = {
  id: string;
  type: LandingFormFieldType;
  label: string;
  placeholder?: string;
  required?: boolean;
  visible?: boolean;
};

export type LandingFormBlock = LandingBlockBase<'form'> & {
  title?: string;
  description?: string;
  buttonLabel: string;
  fields: LandingFormField[];
  successMessage?: string;
  background?: string;
  textColor?: string;
  borderRadius?: number;
  buttonColor?: string;
};

export type LandingFooterSettings = {
  content: string;
  align: Align;
  textColor?: string;
  background?: string;
  fontSize?: number;
  spacing?: number;
  fontFamily?: string;
};

export type LandingBlock =
  | LandingTextBlock
  | LandingImageBlock
  | LandingVideoBlock
  | LandingFormBlock
  | LandingBackgroundBlock;

export type LandingDocument = {
  viewport: LandingViewport;
  blocks: LandingBlock[];
  metadata?: {
    title?: string;
    description?: string;
    defaultFontFamily?: string;
  };
  size?: {
    width: number;
    height: number;
  };
  background?: {
    color?: string | null;
    imageUrl?: string | null;
    overlayColor?: string | null;
    overlayOpacity?: number | null;
  };
  footer: LandingFooterSettings;
};

export const LANDING_DEFAULT_FONT_FAMILY =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif";

const createDefaultFooter = (): LandingFooterSettings => ({
  content: `\u00A9 ${new Date().getFullYear()} La tua azienda. Tutti i diritti riservati.`,
  align: 'center',
  textColor: '#475569',
  background: '#e2e8f0',
  fontSize: 14,
  spacing: 12,
  fontFamily: LANDING_DEFAULT_FONT_FAMILY,
});

export const createEmptyLandingDocument = (): LandingDocument => ({
  viewport: 'mobile',
  blocks: [],
  metadata: {
    defaultFontFamily: LANDING_DEFAULT_FONT_FAMILY,
  },
  background: {
    color: '#f3f4f6',
    imageUrl: null,
    overlayColor: '#0f172a',
    overlayOpacity: 0.35,
  },
  size: {
    width: 430,
    height: 932,
  },
  footer: createDefaultFooter(),
});

export type LandingPageDTO = {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  backgroundColor?: string | null;
  backgroundImageUrl?: string | null;
  backgroundOverlayColor?: string | null;
  backgroundOverlayOpacity?: number | null;
  logoUrl?: string | null;
  primaryColor?: string | null;
  buttonLabel?: string | null;
  successMessage?: string | null;
  eventDate?: string | null;
  reminderDate?: string | null;
  reminderEnabled?: boolean | null;
  reminderEmailSubject?: string | null;
  reminderEmailBody?: string | null;
  couponEmailSubject?: string | null;
  couponEmailBody?: string | null;
  emailSenderName?: string | null;
  customerId?: number | null;
  customerName?: string | null;
  document?: LandingDocument | null;
  published: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  totalViews?: number;
  totalSubmissions?: number;
  totalCouponRedeemed?: number;
};

export type LandingPagePayload = {
  name: string;
  slug: string;
  description?: string | null;
  customerId: number;
  backgroundColor?: string | null;
  backgroundImageUrl?: string | null;
  backgroundOverlayColor?: string | null;
  backgroundOverlayOpacity?: number | null;
  logoUrl?: string | null;
  primaryColor?: string | null;
  buttonLabel?: string | null;
  successMessage?: string | null;
  eventDate?: string | null;
  reminderDate?: string | null;
  reminderEnabled?: boolean | null;
  reminderEmailSubject?: string | null;
  reminderEmailBody?: string | null;
  couponEmailSubject?: string | null;
  couponEmailBody?: string | null;
  emailSenderName?: string | null;
  document: LandingDocument;
  published?: boolean;
};

export type LandingLeadDTO = {
  id: number;
  landingPageId: number;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  phoneNumber?: string | null;
  createdAt?: string | null;
  couponToken?: string | null;
  couponUsed?: boolean;
  couponUsedAt?: string | null;
  couponScanCount?: number;
  couponLastScanAt?: string | null;
};

export type LandingLeadPayload = {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  phoneNumber?: string | null;
};

export type LandingAssetUploadResponse = {
  url: string;
};

export type LandingPageStats = {
  landingPageId: number;
  views: number;
  submissions: number;
  couponRedeemed?: number;
};

export async function listLandingPages(params?: { page?: number; size?: number }) {
  const page = params?.page ?? 0;
  const size = params?.size ?? 10;
  const search = new URLSearchParams({ page: String(page), size: String(size) });
  return apiJson<Page<LandingPageDTO>>(`/p/admin/landing-pages?${search.toString()}`);
}

export async function listLandingPagesForCurrentCustomer(params?: { page?: number; size?: number }) {
  const page = params?.page ?? 0;
  const size = params?.size ?? 10;
  const search = new URLSearchParams({ page: String(page), size: String(size) });
  return apiJson<Page<LandingPageDTO>>(`/p/customer/landing-pages?${search.toString()}`);
}

export async function listLandingLeadsForCurrentCustomer(
  landingPageId: number,
  params?: { page?: number; size?: number },
) {
  const page = params?.page ?? 0;
  const size = params?.size ?? 20;
  const search = new URLSearchParams({ page: String(page), size: String(size) });

  try {
    return await apiJson<Page<LandingLeadDTO>>(
      `/p/customer/landing-pages/${landingPageId}/leads?${search.toString()}`,
    );
  } catch (error) {
    // Fallback: alcune installazioni espongono solo l'endpoint admin; riprovo con quello
    return listLandingLeads(landingPageId, { page, size });
  }
}

export async function getLandingPage(id: number) {
  return apiJson<LandingPageDTO>(`/p/admin/landing-pages/${id}`);
}

export async function createLandingPage(payload: LandingPagePayload) {
  return apiJson<LandingPageDTO>('/p/admin/landing-pages', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateLandingPage(id: number, payload: LandingPagePayload) {
  return apiJson<LandingPageDTO>(`/p/admin/landing-pages/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteLandingPage(id: number) {
  await apiJson<void>(`/p/admin/landing-pages/${id}`, { method: 'DELETE' });
}

export async function listLandingLeads(landingPageId: number, params?: { page?: number; size?: number }) {
  const page = params?.page ?? 0;
  const size = params?.size ?? 20;
  const search = new URLSearchParams({ page: String(page), size: String(size) });
  return apiJson<Page<LandingLeadDTO>>(`/p/admin/landing-pages/${landingPageId}/leads?${search.toString()}`);
}

export async function getLandingStats(landingPageId: number) {
  return apiJson<LandingPageStats>(`/p/admin/landing-pages/${landingPageId}/stats`);
}

async function uploadLandingAsset(endpoint: string, file: File | Blob) {
  const formData = new FormData();
  formData.append('file', file);
  return apiJson<LandingAssetUploadResponse>(endpoint, {
    method: 'POST',
    body: formData,
  });
}

export function uploadLandingLogo(file: File | Blob) {
  return uploadLandingAsset('/p/admin/landing-pages/upload/logo', file);
}

export function uploadLandingBackground(file: File | Blob) {
  return uploadLandingAsset('/p/admin/landing-pages/upload/block', file);
}

export function uploadLandingBlockAsset(file: File | Blob) {
  // Backend fallback: riutilizziamo l'endpoint background finchÃ© /upload/block non Ã¨ disponibile
  return uploadLandingAsset('/p/admin/landing-pages/upload/block', file);
}

export async function fetchPublicLanding(slug: string) {
  return apiJson<LandingPageDTO>(`/g/landing-pages/${encodeURIComponent(slug)}`);
}

export async function submitLandingLead(slug: string, payload: LandingLeadPayload) {
  return apiJson<LandingLeadDTO>(`/g/landing-pages/${encodeURIComponent(slug)}/leads`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export type LandingLeadEmailAvailability = {
  email: string;
  available: boolean;
};

export async function checkLandingLeadEmailAvailability(slug: string, email: string) {
  const params = new URLSearchParams({ email: email.trim() });
  return apiJson<LandingLeadEmailAvailability>(
    `/g/landing-pages/${encodeURIComponent(slug)}/leads/email-availability?${params.toString()}`,
  );
}

export type LandingSlugAvailability = {
  slug: string;
  available: boolean;
};

export async function checkLandingSlugAvailability(
  slug: string,
  options?: { excludeId?: number },
): Promise<LandingSlugAvailability> {
  const trimmed = slug.trim();
  if (!trimmed) {
    throw new Error('Slug non valido');
  }

  const params = new URLSearchParams({ slug: trimmed });
  if (options?.excludeId != null) {
    params.set('excludeId', String(options.excludeId));
  }

  return apiJson<LandingSlugAvailability>(
    `/p/admin/landing-pages/slug-availability?${params.toString()}`,
  );
}

export type ImageSearchResult = {
  id: string;
  thumbnail: string;
  full: string;
  title?: string;
  description?: string;
  provider?: string;
};

const OPENVERSE_ENDPOINT = 'https://api.openverse.org/v1/images/';
const OPENVERSE_PAGE_SIZE = 20;

type OpenverseImage = {
  id?: string;
  identifier?: string;
  url?: string;
  thumbnail?: string;
  title?: string | null;
  description?: string | null;
  provider?: string | null;
  source?: string | null;
  foreign_landing_url?: string | null;
};

type OpenverseResponse = {
  results?: OpenverseImage[];
};

export async function searchImages(query: string): Promise<ImageSearchResult[]> {
  const trimmed = query.trim();
  if (!trimmed) {
    return [];
  }

  const url = new URL(OPENVERSE_ENDPOINT);
  url.searchParams.set('q', trimmed);
  url.searchParams.set('format', 'json');
  url.searchParams.set('page_size', OPENVERSE_PAGE_SIZE.toString());
  url.searchParams.set('license_type', 'commercial');

  try {
    const response = await fetch(url.toString(), {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Openverse search failed with status ${response.status}`);
    }

    const data = (await response.json()) as OpenverseResponse;
    if (!data.results || data.results.length === 0) {
      return [];
    }

    return data.results
      .filter((item): item is OpenverseImage & { url: string } => Boolean(item.url?.trim()))
      .map((item) => {
        const full = item.url!.trim();
        const thumb = item.thumbnail?.trim() ?? full;

        return {
          id: item.id ?? item.identifier ?? full,
          thumbnail: thumb,
          full,
          title: item.title ?? undefined,
          description: item.description ?? undefined,
          provider: item.provider ?? item.source ?? 'Openverse',
        };
      });
  } catch (error) {
    console.warn('Falling back to internal image search due to Openverse error:', error);
    try {
      return await apiJson<ImageSearchResult[]>(
        `/p/admin/landing-pages/search-images?query=${encodeURIComponent(trimmed)}`,
      );
    } catch {
      return [];
    }
  }
}

